import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Copy, Download, Radio } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { stripAnsi } from '@/lib/ansi';
import { cn } from '@/lib/utils';
import type { ProvisioningEvent } from '@/services/provisioning';

const LEVEL_RANK: Record<ProvisioningEvent['level'], number> = {
    debug: 0,
    info: 1,
    warning: 2,
    error: 3,
};

const LEVEL_CLASS: Record<ProvisioningEvent['level'], string> = {
    debug: 'text-slate-500',
    info: 'text-slate-200',
    warning: 'text-amber-300',
    error: 'text-red-400',
};

const FILTERS = [
    { value: 'debug', label: 'Everything' },
    { value: 'info', label: 'Info and above' },
    { value: 'warning', label: 'Warnings and errors' },
    { value: 'error', label: 'Errors only' },
] as const;

interface Props {
    events: ProvisioningEvent[];
    isConnected: boolean;
    isTerminal: boolean;
    truncated?: boolean;
    className?: string;
}

export function ProvisioningLogConsole({
    events,
    isConnected,
    isTerminal,
    truncated,
    className,
}: Props) {
    const [minLevel, setMinLevel] = useState<ProvisioningEvent['level']>('info');
    const scrollRef = useRef<HTMLDivElement>(null);
    // Only follow the tail while the viewer is already at the bottom.
    // Without this, an operator trying to read a failure gets yanked back down
    // every time a batch lands — the classic log-console bug.
    const pinnedRef = useRef(true);

    const visible = useMemo(
        () => events.filter((e) => LEVEL_RANK[e.level] >= LEVEL_RANK[minLevel]),
        [events, minLevel],
    );

    const onScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    }, []);

    useLayoutEffect(() => {
        const el = scrollRef.current;
        if (el && pinnedRef.current) el.scrollTop = el.scrollHeight;
    }, [visible.length]);

    const asText = useCallback(
        () =>
            visible
                .map(
                    (e) =>
                        `${new Date(e.ts).toISOString()} [${e.level}] ${e.task ? `${e.task}: ` : ''}${stripAnsi(e.message)}`,
                )
                .join('\n'),
        [visible],
    );

    const download = useCallback(() => {
        const blob = new Blob([asText()], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'provisioning.log';
        a.click();
        URL.revokeObjectURL(url);
    }, [asText]);

    return (
        <div className={cn('space-y-2', className)}>
            <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                    <Radio
                        className={cn(
                            'h-3.5 w-3.5',
                            isTerminal
                                ? 'text-slate-400'
                                : isConnected
                                    ? 'animate-pulse text-emerald-500'
                                    : 'text-amber-500',
                        )}
                    />
                    {isTerminal ? 'Finished' : isConnected ? 'Live' : 'Polling'}
                    <span className="text-slate-400">· {visible.length} lines</span>
                </div>

                <div className="ml-auto flex items-center gap-2">
                    <Select
                        value={minLevel}
                        onValueChange={(v) => setMinLevel(v as ProvisioningEvent['level'])}
                    >
                        <SelectTrigger className="h-8 w-[180px] text-xs">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {FILTERS.map((f) => (
                                <SelectItem key={f.value} value={f.value}>
                                    {f.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void navigator.clipboard.writeText(asText())}
                    >
                        <Copy className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={download}>
                        <Download className="h-3.5 w-3.5" />
                    </Button>
                </div>
            </div>

            <div
                ref={scrollRef}
                onScroll={onScroll}
                className="h-[480px] overflow-auto rounded-md border border-slate-800 bg-slate-950 p-3 font-mono text-xs leading-relaxed"
            >
                {truncated && (
                    <div className="mb-2 text-slate-500">
                        …earlier output trimmed to keep this page responsive.
                    </div>
                )}
                {visible.length === 0 ? (
                    <div className="text-slate-500">No output yet.</div>
                ) : (
                    visible.map((event) => (
                        <div key={event.counter} className="flex gap-2">
                            <span className="shrink-0 text-slate-600">
                                {new Date(event.ts).toLocaleTimeString()}
                            </span>
                            <span className={cn('min-w-0 break-words', LEVEL_CLASS[event.level])}>
                                {event.type === 'task_start' && event.task ? (
                                    <span className="text-cyan-300">TASK [{event.task}]</span>
                                ) : (
                                    <>
                                        {event.host && (
                                            <span className="text-slate-500">[{event.host}] </span>
                                        )}
                                        {stripAnsi(event.message) ||
                                            (event.task ? `${event.task}: ok` : event.type)}
                                    </>
                                )}
                            </span>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
